import { useState, type ReactNode } from 'react';

import { Box, Button, IconButton, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import AppBar from '@/karehero-app/shell/AppBar';
import { roundButton } from '@/karehero-app/shell/BackChevron';
import { CALCULATOR_BAR } from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';

// PROPOSED. The calculator's own bar: what you are in, and the way out of it.
//
// Every calculator screen carries it, because a flow you can only leave from
// its first screen is a flow people abandon by closing the app. The X asks
// first, and the asking is a hug sheet rather than a warning: the answers are
// kept, so the sheet says that and offers the door.
//
// The bar and the sheet are one component because the X owns the sheet. Split
// them and every screen using the bar would have to hold the same piece of
// state and render the same sheet.
const CalculatorBar = ({
  onLeave,
  initialConfirming = false,
  content = CALCULATOR_BAR,
  back = false,
  divider = true,
  subtitle,
  action,
}: {
  /** Taken when they confirm. Omit on screens where there is nowhere to go. */
  onLeave?: () => void;
  /**
   * Harness-only: open the confirm on first paint, so a flows page can show
   * what the X does without anybody tapping. A mini swallows taps.
   */
  initialConfirming?: boolean;
  /**
   * What flow this is the bar for. The calculator's by default; the AA claim
   * passes its own, because the title is the one thing on every screen of a
   * flow that says which flow you are in.
   */
  content?: typeof CALCULATOR_BAR;
  /**
   * The way out as a back chevron on the left, before the title, rather than
   * an X on the right. It asks first all the same.
   */
  back?: boolean;
  /**
   * The line under the bar. Off where something else is its bottom edge, like
   * the Attendance Allowance claim's progress.
   */
  divider?: boolean;
  /** A line centred under the title, like the stage a claim is on. */
  subtitle?: string;
  /** With back, a control pinned right, opposite the way out. */
  action?: ReactNode;
}) => {
  const [confirming, setConfirming] = useState(initialConfirming);

  return (
    <>
      {/* Left-aligned, on Navigation's gutters: the title sits where the logo
          sits and the X where the avatar sits, so the calculator's bar and the
          app's own bar are the same bar with different contents. */}
      <AppBar
        title={content.title}
        // With back, the title sits in the middle, the chevron to its left.
        align={back ? 'center' : 'left'}
        size='small'
        subtitle={subtitle}
        divider={divider}
        leading={
          back && (
            // A cross, not a back chevron: the calculator is a flow you
            // leave, not a page you go back from. Same white circle.
            <IconButton
              aria-label='Leave the calculator'
              onClick={() => setConfirming(true)}
              sx={roundButton}
            >
              <Icon icon={'xmark' as never} fontSize={16} />
            </IconButton>
          )
        }
        action={
          back ? (
            // 16px in, with AppBar's own 1px, to mirror the way out.
            action && <Box sx={{ mr: '15px' }}>{action}</Box>
          ) : (
            <IconButton
              aria-label='Leave the calculator'
              size='small'
              onClick={() => setConfirming(true)}
            >
              <Icon icon={'xmark' as never} fontSize={18} />
            </IconButton>
          )
        }
      />

      {confirming && (
        <BottomSheet
          variant='hug'
          title={content.leave.title}
          onClose={() => setConfirming(false)}
          footer={(close) => (
            // 16px between the pair. Written as 3 on the assumption that this
            // app reads llama's token array, where index 3 is 16px — it does
            // not: theme.tsx puts spacing back on MUI's 8px scale, so 3 was
            // rendering 24.
            <Stack spacing={2}>
              <Button
                variant='contained'
                size='large'
                fullWidth
                onClick={onLeave ?? close}
              >
                {content.leave.confirm}
              </Button>
              <Button variant='outlined' size='large' fullWidth onClick={close}>
                {content.leave.stay}
              </Button>
            </Stack>
          )}
        >
          <Typography variant='body1'>{content.leave.body}</Typography>
        </BottomSheet>
      )}
    </>
  );
};

export default CalculatorBar;
